import React, { use } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import theme from 'style/theme';
import Divider from 'shared/components/divider/divider';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import PageHeader from 'shared/components/page-header/page-header';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';

import Resident from 'features/residents/models/resident';
import ResidentsNavigation from 'features/residents/resident-navigation/views/residents-navigation';
import AlarmScenariosColumn from 'features/residents/resident-settings/components/columns/alarm-scenarios-column/alarm-scenarios-column';
import DeviceStatusColumn from 'features/residents/resident-settings/components/columns/device-status-column/device-status-column';
import DevicesColumn from 'features/residents/resident-settings/components/columns/devices-column/devices-column';
import OrganizationUnitNameColumn, {
  OrganizationUnitNameColumnHeader,
} from 'features/residents/resident-settings/components/columns/organization-unit-name-column/organization-unit-name-column';
import ResidentSettingDetails from 'features/residents/resident-settings/components/resident-setting-details/resident-setting-details';
import ResidentSettingsBulkEdit from 'features/residents/resident-settings/components/resident-settings-bulk-edit/resident-settings-bulk-edit';
import ResidentSettingsFilters from 'features/residents/resident-settings/components/resident-settings-filters/resident-settings-filters';
import useResidentSettingsTable from 'features/residents/resident-settings/hooks/resident-settings-table';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';
import styles from './resident-settings.module.scss';

const ResidentSettings = (): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');

  const {
    residentSettings,
    residentSettingsIsLoading,
    table,
    detailEditState: { currentResidentSetting, openDetails, viewingMode: detailEditStateViewingMode },
    bulkEditState: { selectedResidentSettings, selectResidentSettings, viewingMode: bulkEditStateViewingMode },
  } = use(ResidentSettingsProviderContext);

  const { tableData, currentItem, selectedRows } = useResidentSettingsTable();

  const columns: ColumnDefinition<Resident, NestedKeyof<Resident>>[] = [
    {
      key: 'organisationUnitName',
      label: t('residentsTable.column.organisationUnitName'),
      renderCustomHeader: () => <OrganizationUnitNameColumnHeader />,
      renderCustomContentProvider: (residentSetting) => (
        <OrganizationUnitNameColumn
          electronicClientRecordName={residentSetting.electronicClientRecordClientResult?.clients?.[0]?.clientName}
          residentSettingName={residentSetting.organisationUnitName}
          breadCrumbs={residentSetting.parentsBreadCrumb}
          isSelected={residentSetting.organisationUnitId === currentResidentSetting?.organisationUnitId}
        />
      ),
      disableSort: true,
    },
    {
      key: 'organisationUnitId',
      label: t('residentsTable.column.alarmScenarios'),
      renderCustomContentProvider: (residentSetting) => (
        <AlarmScenariosColumn organizationUnitScenarios={residentSetting.organisationUnitScenarios} />
      ),
      disableSort: true,
    },
    {
      key: 'devices.length',
      label: t('residentsTable.column.device'),
      renderCustomContentProvider: (residentSetting) => <DevicesColumn deviceLength={residentSetting.devices.length} />,
      disableSort: true,
    },
    {
      key: 'faults.length',
      label: t('residentsTable.column.deviceStatus'),
      renderCustomContentProvider: (residentSetting) => <DeviceStatusColumn faults={residentSetting.faults} />,
      disableSort: true,
    },
  ];

  return (
    <div className={styles.residentSettingsPageWrapper}>
      <div className={styles.residentSettingsCustomPageHeader}>
        <PageHeader title={t('pageTitle')} navigationComponent={<ResidentsNavigation />} hideDivider />
      </div>
      <div className={styles.residentsSettingsContainer} data-testid="residentsSettingsContainer">
        <div
          className={`${styles.residentsSettingsStructureContainer} ${
            (bulkEditStateViewingMode !== 'none' || detailEditStateViewingMode !== 'none') && styles.detailIsOpened
          }`}
          style={{
            transition: theme.transitions.create('all', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shortest,
            }),
          }}
        >
          <ResidentSettingsFilters />
          {selectedResidentSettings.length > 0 && (
            <div className={styles.selectedResidentsContainer}>
              <Typography className={styles.selectedResidentsLabel}>
                {`${selectedResidentSettings.length} ${t('bulkEditScenarios.selected')}`}
              </Typography>
            </div>
          )}
          <Divider />
          {residentSettingsIsLoading && <LoadingIndicator />}
          {!residentSettingsIsLoading && (
            <Table
              data={tableData}
              columns={columns}
              currentItem={currentItem}
              onItemClick={openDetails}
              pagination={{
                count: residentSettings?.total ?? 0,
                onPageChanged: table.handleOnPageChanged,
                rowsPerPage: table.currentRowsPerPage,
                page: table.currentPage,
                onRowsPerPageChanged: table.handleOnRowsPerPageChanged,
              }}
              rowIdentifier={(item: Resident) => item.organisationUnitId}
              rowsAreSelectable
              onSelectedRowsChanged={selectResidentSettings}
              selectedRows={selectedRows}
              onSelectAllRowsChanged={(checked) => selectResidentSettings(checked ? (residentSettings?.residents ?? []) : [])}
            />
          )}
        </div>
        <div className={`${styles.residentDetailContainer} ${detailEditStateViewingMode !== 'none' && styles.detailIsOpened}`}>
          <ResidentSettingDetails />
        </div>

        <div
          className={`${styles.residentDetailContainer} ${detailEditStateViewingMode === 'none' && bulkEditStateViewingMode !== 'none' && styles.detailIsOpened}`}
        >
          <ResidentSettingsBulkEdit />
        </div>
      </div>
    </div>
  );
};

export default ResidentSettings;
